import { z } from 'zod';
import { reusePolicySchema } from './reuse';
import { audienceSchema, audienceScopeSchema } from './audience';
import { timeZoneSchema } from './time';
import { levelSchema, tagsSchema, topicsSchema } from './topics';

/*
 * Live events: workshops (one or more sessions) and webinars, scheduled ahead.
 * Each session is in person, online (a meeting link) or hybrid (both).
 * Learners register (with an optional seat limit and a waitlist), get a
 * calendar invite and reminders, and check in to each session; attending
 * enough sessions issues a certificate of participation.
 */

export const EVENT_KINDS = ['workshop', 'webinar'] as const;
export type EventKind = (typeof EVENT_KINDS)[number];
export const EVENT_KIND_LABELS: Record<EventKind, string> = {
  workshop: 'Workshop',
  webinar: 'Webinar',
};

export const SESSION_FORMATS = ['online', 'in_person', 'hybrid'] as const;
export type SessionFormat = (typeof SESSION_FORMATS)[number];
export const SESSION_FORMAT_LABELS: Record<SessionFormat, string> = {
  online: 'Online',
  in_person: 'In person',
  hybrid: 'In person and online',
};

export const EVENT_STATUSES = ['draft', 'published', 'cancelled', 'archived'] as const;
export type EventStatus = (typeof EVENT_STATUSES)[number];

export const REGISTRATION_STATUSES = ['registered', 'waitlisted', 'cancelled'] as const;
export type RegistrationStatus = (typeof REGISTRATION_STATUSES)[number];

/** Join links show from this many minutes before a session until it ends. */
export const JOIN_OPENS_MINUTES = 30;
/** Self check-in is open from this many minutes before a session until this long after it ends. */
export const CHECK_IN_OPENS_MINUTES = 15;
export const CHECK_IN_CLOSES_MINUTES = 30;
/**
 * Reminders before each session. The job runs hourly, so the second one is
 * due two hours ahead and goes out one to two hours before the start.
 */
export const EVENT_REMINDERS = [
  { kind: 'day', minutesBefore: 24 * 60 },
  { kind: 'soon', minutesBefore: 120 },
] as const;
export type EventReminderKind = (typeof EVENT_REMINDERS)[number]['kind'];

export const MAX_SESSIONS = 20;

const httpsUrl = z
  .string()
  .trim()
  .url()
  .max(1000)
  .refine((u) => u.startsWith('https://'), 'Use an https:// link');

export const eventSettingsSchema = z.object({
  /** Seats; null for no limit. Past the limit, people join a waitlist. */
  capacity: z.number().int().min(1).max(100_000).nullable().default(null),
  certificate: z.boolean().default(true),
  /** Sessions attended for a certificate; null means all of them. */
  minSessions: z.number().int().min(1).max(MAX_SESSIONS).nullable().default(null),
});
export type EventSettings = z.infer<typeof eventSettingsSchema>;

export const eventInputSchema = z.object({
  kind: z.enum(EVENT_KINDS).default('workshop'),
  title: z.string().trim().min(3).max(200),
  summary: z.string().trim().max(500).optional(),
  /** Markdown. */
  description: z.string().max(20_000).optional(),
  /** Who's presenting, e.g. "Dr. A. Rao, IIT Hyderabad". */
  hosts: z.string().trim().max(300).optional(),
  audience: audienceSchema.default('org'),
  audienceScope: audienceScopeSchema.nullable().optional(),
  reuse: reusePolicySchema.nullable().optional(),
  /** Times are entered and shown in this zone (and in each viewer's own). */
  timeZone: timeZoneSchema,
  level: levelSchema.default('beginner'),
  topics: topicsSchema,
  tags: tagsSchema,
  coverAssetId: z.string().uuid().nullable().optional(),
  settings: eventSettingsSchema.default({}),
});
export type EventInput = z.infer<typeof eventInputSchema>;

export const sessionInputSchema = z
  .object({
    title: z.string().trim().max(200).optional(),
    startsAt: z.coerce.date(),
    durationMinutes: z
      .number()
      .int()
      .min(5)
      .max(24 * 60),
    format: z.enum(SESSION_FORMATS),
    /** Venue name, e.g. "Seminar Hall 2, Main Building". */
    venue: z.string().trim().max(300).optional(),
    /** Street address or directions, shown to anyone who can see the event. */
    address: z.string().trim().max(500).optional(),
    /** Zoom, Meet, Teams or any https link: only registered attendees see it, near the start. */
    meetingUrl: httpsUrl.optional(),
    /** Passcode, dial-in numbers and the like; shown with the link. */
    meetingDetails: z.string().trim().max(1000).optional(),
  })
  .superRefine((s, ctx) => {
    if (s.format !== 'online' && !s.venue)
      ctx.addIssue({ code: 'custom', path: ['venue'], message: 'Add the venue' });
    if (s.format !== 'in_person' && !s.meetingUrl)
      ctx.addIssue({ code: 'custom', path: ['meetingUrl'], message: 'Add the meeting link' });
  });
export type SessionInput = z.infer<typeof sessionInputSchema>;

export const checkInSchema = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9]{4,8}$/),
});

export const attendanceInputSchema = z.object({
  userId: z.string().uuid(),
  attended: z.boolean(),
});

export const registerSchema = z.object({
  /** The browser's zone, for the dates in confirmation and reminder emails. */
  timeZone: timeZoneSchema.optional(),
});
